import { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Send, Clock, CheckCircle2, XCircle, Undo2, AlertTriangle, Loader2, Mail, Briefcase, Lock, RefreshCw,
} from 'lucide-react';
import { useEmployerInvites, type EmployerInvite } from '@/hooks/useEmployerInvites';
import { usePermissions } from '@/hooks/usePermissions';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useInviteScoreStates } from '@/hooks/useTalentDiscoveryMeter';
import { InviteScoreStateBadge } from '@/components/talent-discovery/InviteScoreStateBadge';
import { TalentDiscoveryMeter } from '@/components/talent-discovery/TalentDiscoveryMeter';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatCandidateId } from '@/lib/candidateId';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { SAFE_COMPACT_VALUE, SAFE_TEXT_WRAP } from '@/lib/responsiveText';

const statusConfig: Record<string, { labelKey: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof Clock }> = {
  pending: { labelKey: 'hire.talent.invitations.statuses.pending', variant: 'default', icon: Clock },
  're-invited': { labelKey: 'hire.talent.invitations.statuses.reInvited', variant: 'default', icon: RefreshCw },
  accepted: { labelKey: 'hire.talent.invitations.statuses.accepted', variant: 'secondary', icon: CheckCircle2 },
  declined: { labelKey: 'hire.talent.invitations.statuses.declined', variant: 'destructive', icon: XCircle },
  withdrawn: { labelKey: 'hire.talent.invitations.statuses.withdrawn', variant: 'outline', icon: Undo2 },
  expired: { labelKey: 'hire.talent.invitations.statuses.expired', variant: 'destructive', icon: AlertTriangle },
};

function InvitationsManagement() {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const { hasAccess: hasTalentPool } = useServiceAccess(SERVICE_KEYS.TALENT_POOL);
  const { enabled: governanceV2 } = useFeatureFlag('talent_discovery_governance_v2');
  const { invites, isLoading, stats, withdraw, isWithdrawing } = useEmployerInvites();
  const { scoreStates } = useInviteScoreStates(governanceV2);
  const [withdrawDialog, setWithdrawDialog] = useState<EmployerInvite | null>(null);
  const [tab, setTab] = useState('all');

  if (!hasTalentPool) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
          <Lock className="w-12 h-12 text-muted-foreground/40" />
          <h2 className="text-xl font-semibold text-foreground">{t('hire.talent.invitations.serviceUnavailableTitle')}</h2>
          <p className="text-muted-foreground max-w-md">
            {t('hire.talent.invitations.serviceUnavailableDescription')}
          </p>
        </div>
      </AppLayout>
    );
  }

  const filtered = tab === 'all' ? invites : invites.filter(i => i.invite_status === tab);

  const handleWithdraw = () => {
    if (!withdrawDialog) return;
    withdraw(withdrawDialog.id);
    setWithdrawDialog(null);
  };

  const formatInviteDate = (date: string) => formatLocalizedDate(date, 'MMM d, yyyy', i18n.language);

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-5 sm:p-6 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground ${SAFE_TEXT_WRAP}`}>{t('hire.talent.invitations.title')}</h1>
              <p className={`text-muted-foreground mt-1.5 text-sm ${SAFE_TEXT_WRAP}`}>{t('hire.talent.invitations.subtitle')}</p>
            </div>
          </div>
        </Card>

        {governanceV2 && <TalentDiscoveryMeter variant="card" />}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[
            { label: t('hire.talent.invitations.stats.total'), value: stats.total, color: 'text-foreground' },
            { label: t('hire.talent.invitations.statuses.pending'), value: stats.pending, color: 'text-primary' },
            { label: t('hire.talent.invitations.statuses.accepted'), value: stats.accepted, color: 'text-success' },
            { label: t('hire.talent.invitations.statuses.declined'), value: stats.declined, color: 'text-destructive' },
            { label: t('hire.talent.invitations.statuses.withdrawn'), value: stats.withdrawn, color: 'text-muted-foreground' },
            { label: t('hire.talent.invitations.statuses.expired'), value: stats.expired, color: 'text-destructive' },
          ].map(s => (
            <Card key={s.label} className="rounded-xl">
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className={`text-xs text-muted-foreground mt-0.5 ${SAFE_COMPACT_VALUE}`}>{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid h-auto w-full grid-cols-2 rounded-xl sm:grid-cols-3 xl:grid-cols-6">
            <TabsTrigger value="all" className="min-h-10 rounded-lg whitespace-normal">{t('hire.talent.invitations.tabs.all', { count: stats.total })}</TabsTrigger>
            <TabsTrigger value="pending" className="min-h-10 rounded-lg whitespace-normal">{t('hire.talent.invitations.tabs.pending', { count: stats.pending })}</TabsTrigger>
            <TabsTrigger value="accepted" className="min-h-10 rounded-lg whitespace-normal">{t('hire.talent.invitations.tabs.accepted', { count: stats.accepted })}</TabsTrigger>
            <TabsTrigger value="declined" className="min-h-10 rounded-lg whitespace-normal">{t('hire.talent.invitations.tabs.declined', { count: stats.declined })}</TabsTrigger>
            <TabsTrigger value="expired" className="min-h-10 rounded-lg whitespace-normal">{t('hire.talent.invitations.tabs.expired', { count: stats.expired })}</TabsTrigger>
            <TabsTrigger value="withdrawn" className="min-h-10 rounded-lg whitespace-normal">{t('hire.talent.invitations.tabs.withdrawn', { count: stats.withdrawn })}</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4 space-y-3">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && filtered.length === 0 && (
              <Card className="rounded-xl">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <Mail className="w-10 h-10 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('hire.talent.invitations.empty')}</p>
                </CardContent>
              </Card>
            )}

            {filtered.map(invite => {
              const cfg = statusConfig[invite.invite_status] || statusConfig.pending;
              const StatusIcon = cfg.icon;
              const isPending = invite.invite_status === 'pending' || invite.invite_status === 're-invited';
              const daysLeft = invite.expires_at
                ? differenceInDays(new Date(invite.expires_at), new Date())
                : null;
              const isExpiredByTime = daysLeft !== null && daysLeft < 0;
              const showAsExpired = invite.invite_status === 'expired' || (isPending && isExpiredByTime);
              return (
                <Card key={invite.id} className={`rounded-xl hover:shadow-card-hover transition-shadow ${showAsExpired ? 'opacity-60' : ''}`}>
                  <CardContent className="flex flex-wrap items-start gap-3 p-4 sm:items-center sm:gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${showAsExpired ? 'bg-destructive/10' : 'bg-primary/10'}`}>
                      <Briefcase className={`w-5 h-5 ${showAsExpired ? 'text-destructive' : 'text-primary'}`} />
                    </div>

                    <div className="min-w-0 flex-1 basis-[calc(100%-3.25rem)] sm:basis-auto">
                      <p className="font-semibold text-foreground text-sm truncate">
                        {invite.candidate_name
                          || formatCandidateId(invite.seek_candidate_number, invite.seek_candidate_number)
                          || t('hire.talent.invitations.candidateFallback', { id: invite.candidate_id.slice(0, 8) })}
                      </p>
                      <p className="text-xs text-primary font-medium mt-0.5 truncate">
                        {invite.job_title || t('hire.talent.invitations.generalInvite')}
                      </p>
                      {invite.seek_candidate_number && (
                        <p className="text-[11px] text-muted-foreground font-mono">
                          {formatCandidateId(invite.seek_candidate_number, invite.seek_candidate_number)}
                        </p>
                      )}
                      <div className="mt-0.5 flex flex-wrap items-center gap-2">
                        <p className={`text-xs text-muted-foreground ${SAFE_COMPACT_VALUE}`}>
                          {t('hire.talent.invitations.sentDate', { date: formatInviteDate(invite.created_at) })}
                          {invite.responded_at && (
                            <span> · {t('hire.talent.invitations.respondedDate', { date: formatInviteDate(invite.responded_at) })}</span>
                          )}
                        </p>
                        {isPending && !showAsExpired && daysLeft !== null && (
                          <span className={`text-[10px] font-medium ${daysLeft <= 1 ? 'text-destructive' : daysLeft <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                            · {daysLeft <= 0 ? t('hire.talent.invitations.expiresToday') : t('hire.talent.invitations.daysLeft', { count: daysLeft })}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ms-[52px] flex flex-shrink-0 flex-col items-start gap-1 sm:ms-0 sm:items-end">
                      <Badge variant={showAsExpired ? 'destructive' : cfg.variant} className="rounded-lg text-xs gap-1">
                        {showAsExpired ? <AlertTriangle className="w-3 h-3" /> : <StatusIcon className="w-3 h-3" />}
                        {showAsExpired ? t('hire.talent.invitations.statuses.expired') : t(cfg.labelKey)}
                      </Badge>
                      {governanceV2 && invite.invite_status === 'accepted' && (
                        <InviteScoreStateBadge state={scoreStates[invite.id]} />
                      )}
                    </div>

                    {isPending && !showAsExpired && can('talent_pool', 'invite') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ms-[52px] flex-shrink-0 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive sm:ms-0"
                        onClick={() => setWithdrawDialog(invite)}
                        disabled={isWithdrawing}
                      >
                        <Undo2 className="w-3.5 h-3.5 me-1" />
                        {t('hire.talent.invitations.withdraw')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>
      </div>

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={!!withdrawDialog} onOpenChange={() => setWithdrawDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              {t('hire.talent.invitations.withdrawTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('hire.talent.invitations.withdrawDescriptionPrefix')}{' '}
              <strong>{withdrawDialog?.job_title || t('hire.talent.invitations.thisPosition')}</strong>
              ? {t('hire.talent.invitations.withdrawDescriptionSuffix')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWithdrawDialog(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleWithdraw} disabled={isWithdrawing}>
              {isWithdrawing && <Loader2 className="w-4 h-4 animate-spin me-2" />}
              {t('hire.talent.invitations.withdrawTitle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default InvitationsManagement;
